<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <!-- Per Page -->
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ path: '/settings' }"
            >
              <i class="fa-solid fa-arrow-left"></i>
              <span class="ml-25">Back</span>
            </b-button>

            <h3 class="ml-1">Pillar Titles</h3>
          </b-col>

          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <div class="d-flex align-items-center mr-1">
                <label>Show</label>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label>entries </label>
              </div>

              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                cle
                placeholder="Search..."
              />
              <b-button
                v-if="$Can('add-pillar-title')"
                variant="primary"
                @click="addPillarTitle()"
              >
                <span class="text-nowrap">Add Pillar Title</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refActionListTable"
          :outlined="true"
          :fields="fields"
          :items="filteredPillarTitles"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="$Can('edit-pillar-title') && editPillarTitle($event.id)"
        >
          <template #cell(pillar)="data">
            <div
              :style="{
                backgroundColor: data.item.pillar.color
                  ? data.item.pillar.color
                  : '#fff',
              }"
              style="font-size: 1rem;font-weight: bold;"
              class="p-25"
            >
              <p>
                {{ data.item.pillar.title ? data.item.pillar.title : "-" }}
              </p>
            </div>
          </template>

          <template #cell(title)="data">
            <div style="font-size: 1rem;" class="p-25">
              <p>{{ data.value }}</p>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status == 'Active'"
              pill
              :variant="`light-success`"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>

            <b-badge
              v-else
              pill
              :variant="`light-danger`"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div>
              <b-button
                v-if="$Can('edit-pillar-title')"
                variant="warning"
                class="mr-25 p-50"
                @click="editPillarTitle(data.item.id)"
              >
                <i class="fa-solid fa-pen-to-square mr-25"></i>
                <span> Edit</span>
              </b-button>

              <b-button
                v-if="$Can('change-status-pillar-title')"
                variant="primary"
                @click="changeStatus(data.item.id)"
                class="mr-25 p-50"
                v-b-tooltip.hover.bottom="
                  data.item.status === 'Active'
                    ? 'If you passive this pillar title, it will not show in the list'
                    : 'If you active this pillar title, it will show in the list'
                "
              >
                <span>{{
                  data.item.status === "Active" ? "Passive" : "Active"
                }}</span>
              </b-button>

              <b-button
                v-if="$Can('delete-pillar-title')"
                variant="danger"
                @click="deletePillarTitle(data.item.id)"
                class="p-50"
              >
                <span>Delete</span>
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Add pagination below table -->
        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormRating,
} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormRating,
    vSelect,
    ToastificationContent,
  },

  setup() {
    const PILLAR_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(PILLAR_APP_STORE_MODULE_NAME))
      store.registerModule(PILLAR_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PILLAR_APP_STORE_MODULE_NAME))
        store.unregisterModule(PILLAR_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      pillarTitles: [],
      filter: null,
      filterOn: ["pillar", "title"],
      loading: false,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 50, 100],
      perPage: localStorage.getItem("pillarTitleShow") ?? 50,
      currentPage: 1,
      totalRows: 0,

      perPageOptions: [10, 25, 50, 100],
      fields: [
        {
          key: "pillar",
          sortable: true,
          class: "text-left",
          thStyle: "width:30%;color:white!important",
          sortByFormatted: true,
          formatter: (value) => value?.title || "-",
        },
        {
          key: "title",
          sortable: true,
          class: "text-left",
          thStyle: "width:40%;color:white!important",
        },
        {
          key: "status",
          sortable: false,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
        },
        {
          key: "actions",
          sortable: false,
          class: "text-center",
          thStyle: "width:20%;color:white!important",
        },
      ],
    };
  },

  watch: {},

  methods: {
    getPillarTitles() {
      this.loading = true;
      store
        .dispatch("settings/getAllPillarTitles")
        .then((res) => {
          this.pillarTitles = res.data;
          this.totalRows = res.data.length;
          this.loading = false;
        })
        .catch((error) => {
          this.$router.push({ name: "settings" });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "An error occurred",
              text: "Please try again later or contact support.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    editPillarTitle(id) {
      this.$router.push({
        name: "settings-pillar-title-edit",
        params: { id: id },
      });
    },

    changeStatus(id) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want change status of this pillar title?",
          {
            size: "sm",
            title: "Confirm Deletion",
            okVariant: "primary",
            okTitle: "Yes",
            cancelTitle: "No",
            cancelVariant: "outline-secondary",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.loading = true;
            const pillarTitle = this.pillarTitles.find((met) => met.id === id);

            if (pillarTitle) {
              store
                .dispatch("settings/changeStatusPillarTitle", pillarTitle)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successful",
                      text: "✔️ Pillar status changed Successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getPillarTitles();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    async deletePillarTitle(id) {
      const usage = await this.checkPillarTitleUsage(id);

      let message = "Are you sure you want delete of this pillar title?";
      if (usage > 0) {
        message =
          "There are uses related to this pillar title. Do you still want to delete it? All related data will be deleted!";
      }
      this.$bvModal
        .msgBoxConfirm(message, {
          size: "sm",
          title: "Confirm Deletion",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.loading = true;
            const pillarTitle = this.pillarTitles.find((met) => met.id === id);

            if (pillarTitle) {
              store
                .dispatch("settings/deletePillarTitle", pillarTitle)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successfully",
                      text: "✔️ Pillar title deleted Successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getPillarTitles();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    checkPillarTitleUsage(id) {
      return new Promise((resolve, reject) => {
        store.dispatch("settings/checkPillarTitleUsage", id).then((res) => {
          resolve(res.data.usage_count);
        });
      });
    },

    addPillarTitle() {
      this.$router.push({ name: "settings-pillar-title-add" });
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
  },

  created() {
    this.getPillarTitles();
  },

  computed: {
    dataMeta() {
      return {
        from:
          this.totalRows != 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) <
          this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
    filteredPillarTitles() {
      let filtered = this.pillarTitles;

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        filtered = this.pillarTitles.filter(
          (item) =>
            item.title.toLowerCase().includes(searchTerm) ||
            (item.pillar.title &&
              item.pillar.title.toLowerCase().includes(searchTerm))
        );
      }

      return filtered;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.table-responsive.b-table-sticky-header {
  max-height: 75vh !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
